/**
 * Time slots ("09:00-10:30"), days and timestamps, as the screens show them.
 * The rules themselves (what can be booked, which status may follow which)
 * are the server's: backend/scheduling.py. Nothing here decides anything; it
 * only helps a form refuse an obvious mistake before sending it.
 */

// Weekdays as the server counts them: 0 = Monday … 6 = Sunday.
export const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];

export const toMinutes = (time) => {
  const [h, m] = String(time).split(":").map(Number);
  return h * 60 + m;
};
export const fromMinutes = (n) => `${String(Math.floor(n / 60)).padStart(2, "0")}:${String(n % 60).padStart(2, "0")}`;
export const slotRange = (slot) => String(slot).split("-").map(toMinutes);
export const sortSlots = (slots) => [...slots].sort((a, b) => slotRange(a)[0] - slotRange(b)[0]);

/** The slot in `slots` that overlaps `slot` (touching is not overlapping), if any. */
export const overlapping = (slots, slot) => {
  const [start, end] = slotRange(slot);
  return slots.find((other) => {
    const [s, e] = slotRange(other);
    return start < e && s < end;
  });
};

/** Back-to-back slots of `minutes` each from `start` to `end`, with `gap` minutes between them. */
export function generateSlots(start, end, minutes, gap = 0) {
  const last = toMinutes(end);
  const slots = [];
  if (!(minutes > 0) || gap < 0) return slots;
  for (let at = toMinutes(start); at + minutes <= last && slots.length < 48; at += minutes + gap) {
    slots.push(`${fromMinutes(at)}-${fromMinutes(at + minutes)}`);
  }
  return slots;
}

/** Today (or a day near it) on this device's clock, as YYYY-MM-DD. */
export const localDay = (offsetDays = 0) => {
  const d = new Date(Date.now() + offsetDays * 86400000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
export const addDays = (day, n) => {
  const d = new Date(`${day}T12:00:00`);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** "2026-10-14" -> { weekday: "Wed", day: "14", month: "Oct" } */
export const dayParts = (day) => {
  const d = new Date(`${day}T12:00:00`);
  return {
    weekday: d.toLocaleDateString("en-IN", { weekday: "short" }),
    day: String(d.getDate()),
    month: d.toLocaleDateString("en-IN", { month: "short" }),
  };
};
export const formatDay = (day) => (day ? new Date(`${day}T12:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" }) : "");

/** A recorded moment (ISO, from the server) in this device's time: "12 Oct, 10:05 am". */
export const formatMoment = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "");

/** Time between two recorded moments: "57 min", "1 h 20 min". */
export const formatDuration = (from, to) => {
  if (!from || !to) return "";
  const minutes = Math.max(0, Math.round((new Date(to) - new Date(from)) / 60000));
  if (minutes < 1) return "under a minute";
  return minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
};

/** A service's booking settings in a few words, for a list. */
export const scheduleSummary = (schedule) => {
  const s = schedule || {};
  const days = s.days?.length && s.days.length < 7 ? s.days.map((d) => DAY_LABELS[d]).join(" ") : "Every day";
  const parts = [s.slots?.length ? `${s.slots.length} own slot${s.slots.length === 1 ? "" : "s"}` : "General slots", days];
  if (s.available_from) parts.push(`from ${s.available_from}`);
  if (s.available_until) parts.push(`until ${s.available_until}`);
  if (s.capacity) parts.push(`${s.capacity} per slot`);
  return parts.join(" · ");
};

const WORDS = {
  pending: "Waiting to be accepted", confirmed: "Confirmed", assigned: "Assigned", rescheduled: "To be rescheduled",
  in_progress: "In progress", completed: "Completed", cancelled: "Cancelled", rejected: "Rejected",
};
export const statusLabel = (status) => WORDS[status] || String(status || "").replace("_", " ");

/** One line of a booking's history, in words. */
const ACTIONS = {
  created: "Booked", accepted: "Accepted", rejected: "Rejected", reschedule_requested: "Reschedule requested",
  started: "Task started", ended: "Task ended", cancelled: "Cancelled", seller_assigned: "Professional assigned",
};
const ROLES = { customer: "customer", seller: "professional", tenant_admin: "business admin", super_admin: "platform admin" };
export const historyLine = (h) => {
  const what = h.action === "status_changed" ? `Changed to ${statusLabel(h.status).toLowerCase()}` : (ACTIONS[h.action] || h.action);
  return `${what}${h.note ? ` (${h.note})` : ""}${h.by_role ? ` — by the ${ROLES[h.by_role] || h.by_role}` : ""}`;
};

/** A request id that makes a repeated "place booking" request one booking on the server. */
export const newRequestId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`);
